import { Text } from '$tablet-app/src/app/components/Text/Text';
import useRecordImage from '$tablet-app/src/app/images/hooks/useRecordImage';
import { layout, margins, palette, spacing } from '$tablet-app/src/app/theme';
import { Asset } from 'expo-asset';
import React, { useState } from 'react';
import {
  FlatList,
  Image,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { ProfileAvatars } from '../../../../../assets/profilePictures';
import SelectImageButton from '../../../components/SelectImageButton/SelectImageButton';
import SelectedProfileImage from './SelectedProfileImage';
import { LocalImage } from '$tablet-app/src/app/images/types';

interface Props {
  profileColor: string;
  user: any;
  style?: StyleProp<ViewStyle>;
  onChangeProfilePicture: (params: {
    profilePicture: string | null;
    image: LocalImage | null;
  }) => Promise<any>;
}

// get library profile pictures
const PROFILE_PIC_DATA: any[][] = [];
const ProfileAvatarsEntries = Object.entries(ProfileAvatars);
const numRows = 3;

for (let i = 0; i < ProfileAvatarsEntries.length; i += numRows) {
  let entriesSubset = ProfileAvatarsEntries.slice(i, i + numRows);
  if (entriesSubset.length < numRows) {
    entriesSubset = entriesSubset.concat(
      Array(numRows - entriesSubset.length).fill(undefined)
    );
  }

  PROFILE_PIC_DATA.push(
    entriesSubset.map((entry) => {
      if (!entry) {
        return undefined;
      }

      const [key, value] = entry;
      return { id: 'id_' + key, uri: Asset.fromModule(value).uri };
    })
  );
}

const ProfilePictureSelectionView = (props: Props) => {
  const {
    profileColor,
    onChangeProfilePicture,
    user,
    style: styleOverride,
  } = props;

  const [selectedId, setSelectedId] = useState(null);
  const [profilePicture, setProfilePicture] = useState(
    ProfileAvatars[user.profilePicture || 'profile_avatar_1']
  );

  const { uri } = useRecordImage({ record: user });

  const pictureUri = uri
    ? typeof uri === 'number'
      ? uri
      : { uri }
    : profilePicture;

  const updateProfilePicture = async (item: any) => {
    setProfilePicture(item);
    setSelectedId(item.id);

    await onChangeProfilePicture({
      profilePicture: item.id.replace('id_', ''),
      image: null,
    });
  };

  // opens window to pick image, asks permission if needed
  const chooseProfileImage = async (imageUpload: LocalImage) => {
    await onChangeProfilePicture({
      profilePicture: null,
      image: imageUpload,
    });
  };

  const renderProfilePictureItem = (item?: any, key?: string) => {
    return (
      <ProfilePictureItem
        key={key}
        item={item}
        onPress={() => {
          if (item) {
            updateProfilePicture(item);
          }
        }}
        isSelected={item?.id === selectedId}
      />
    );
  };

  const { width } = useWindowDimensions();

  const isSmall = width < 600;
  const sizeStyles = isSmall ? smallStyles : largeStyles;

  return (
    <View style={[styles.container, sizeStyles.container, styleOverride]}>
      <View
        style={[
          styles.profilePictureContainer,
          sizeStyles.profilePictureContainer,
        ]}
      >
        <Text preset="bold" style={styles.profilePictureHeader}>
          Profile Picture
        </Text>

        <SelectedProfileImage
          profileColor={profileColor}
          source={pictureUri}
          style={styles.selectedProfileImage}
        />

        <View style={layout.centeredRow}>
          <SelectImageButton
            style={styles.selectImageButton}
            onSelect={chooseProfileImage}
          />
        </View>
      </View>

      <View
        style={[
          styles.profilePickerContainer,
          sizeStyles.profilePickerContainer,
        ]}
      >
        <Text style={styles.profilePicturePickerHeader}>
          You can choose a profile picture from our library
        </Text>
        <FlatList
          style={styles.profilePictureFlatlist}
          data={PROFILE_PIC_DATA}
          renderItem={({ item }) => {
            return (
              <View style={styles.profilePictureImageColumn}>
                {item.map((elem, index) =>
                  renderProfilePictureItem(elem, `${elem?.id}_${index}`)
                )}
              </View>
            );
          }}
          keyExtractor={(item, index) => `fl_${item?.[0]?.id}_${index}`}
          nestedScrollEnabled
          horizontal
        />
      </View>
    </View>
  );
};

// individual list items in the profile picture selection list
const ProfilePictureItem = ({ item, onPress, isSelected }: any) => {
  const [retryCount, setRetryCount] = useState(0);

  return (
    <TouchableOpacity
      onPress={onPress}
      style={[
        styles.profilePictureImageContainer,
        {
          backgroundColor: isSelected ? palette.Outline_blue : palette.white,
        },
      ]}
      disabled={!item}
    >
      {item ? (
        <Image
          key={`image-${retryCount}`}
          style={styles.profilePicturePickerImage}
          source={{ uri: item.uri }}
          onError={() => {
            if (retryCount < 3) {
              setRetryCount((oldVal) => oldVal + 1);
            }
          }}
          onLoad={() => setRetryCount(0)}
        />
      ) : null}
    </TouchableOpacity>
  );
};

export default ProfilePictureSelectionView;

const styles = StyleSheet.create({
  container: {
    justifyContent: 'flex-start',
    width: '100%',
    rowGap: spacing[2],
  },
  profilePictureContainer: {
    alignItems: 'center',
    justifyContent: 'space-around',
    marginHorizontal: spacing[2],
  },
  profilePictureHeader: {
    marginLeft: 50,
    ...margins.mb6,
    width: '100%',
  },
  selectedProfileImage: {
    flex: 1,
  },
  selectImageButton: {
    marginTop: spacing[2],
  },
  profilePickerContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  profilePicturePickerHeader: {
    paddingHorizontal: spacing[2],
  },
  profilePictureFlatlist: {
    width: '100%',
  },
  profilePictureImageColumn: {
    justifyContent: 'space-evenly',
  },
  profilePictureImageContainer: {
    borderRadius: 100 / 2,
    height: 100,
    overflow: 'hidden',
    padding: 10,
    width: 100,
  },
  profilePicturePickerImage: {
    borderRadius: 80 / 2,
    height: 80,
    width: 80,
    overflow: 'hidden',
  },
});

const smallStyles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  profilePictureContainer: {
    width: '100%',
  },
  profilePickerContainer: {
    width: '100%',
  },
});

const largeStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  profilePictureContainer: {
    flex: 0.5,
    minWidth: 250,
  },
  profilePickerContainer: {
    flex: 1,
    minWidth: 250,
  },
});
